package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PrepManagementScreen(
    onBack: () -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }

    var masterItems by remember { mutableStateOf<List<PrepItem>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }

    var showAddDialog by remember { mutableStateOf(false) }
    var itemToEdit by remember { mutableStateOf<PrepItem?>(null) }
    var itemToDelete by remember { mutableStateOf<PrepItem?>(null) }

    fun loadMasterItems() {
        scope.launch {
            isLoading = true
            repository.getMasterPrepItems().onSuccess { items ->
                masterItems = items
            }.onFailure {
                masterItems = emptyList()
            }
            isLoading = false
        }
    }

    LaunchedEffect(Unit) {
        loadMasterItems()
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { Text("Manage Master Prep Items") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        BackArrowIcon()
                    }
                },
                actions = {
                    ToolbarPlusButton(onClick = { showAddDialog = true })
                }
            )
        }
    ) { padding ->
        Box(modifier = Modifier.padding(padding).fillMaxSize()) {
            if (isLoading) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    BrandLoadingSpinner()
                }
            } else if (masterItems.isEmpty()) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Text("No master prep items defined.", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.outline)
                        Button(onClick = { showAddDialog = true }) {
                            Text("Add First Item")
                        }
                    }
                }
            } else {
                LazyColumn(
                    modifier = Modifier.fillMaxSize().padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    items(masterItems, key = { it.id }) { item ->
                        Card(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { itemToEdit = item },
                            shape = MaterialTheme.shapes.medium,
                            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(16.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                Column(modifier = Modifier.weight(1f)) {
                                    Text(
                                        text = item.itemName,
                                        style = MaterialTheme.typography.titleMedium,
                                        fontWeight = FontWeight.Bold
                                    )
                                    if (!item.shelfLife.isNullOrBlank()) {
                                        Text("Shelf Life: ${item.shelfLife}", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.secondary)
                                    }
                                    if (!item.prepTimeHour.isNullOrBlank()) {
                                        Text("Prep Time: ${item.prepTimeHour}", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.secondary)
                                    }
                                    if (!item.notes.isNullOrBlank()) {
                                        Text("Notes: ${item.notes}", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.outline)
                                    }
                                }

                                IconButton(onClick = { itemToDelete = item }) {
                                    CloseIcon(tint = MaterialTheme.colorScheme.error)
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    if (showAddDialog) {
        MasterPrepItemDialog(
            title = "Add Master Prep Item",
            onDismiss = { showAddDialog = false },
            onConfirm = { name, shelfLife, prepTime, notes ->
                scope.launch {
                    repository.saveMasterPrepItem(
                        SavePrepItemRequest(
                            itemName = name,
                            shelfLife = shelfLife,
                            prepTimeHour = prepTime,
                            notes = notes
                        )
                    ).onSuccess {
                        showAddDialog = false
                        loadMasterItems()
                        snackbarHostState.showSnackbar("Master prep item saved successfully")
                    }
                }
            }
        )
    }

    itemToEdit?.let { item ->
        MasterPrepItemDialog(
            title = "Edit Master Prep Item",
            initialItem = item,
            onDismiss = { itemToEdit = null },
            onConfirm = { name, shelfLife, prepTime, notes ->
                scope.launch {
                    repository.saveMasterPrepItem(
                        SavePrepItemRequest(
                            id = item.id,
                            itemName = name,
                            shelfLife = shelfLife,
                            prepTimeHour = prepTime,
                            notes = notes
                        )
                    ).onSuccess {
                        itemToEdit = null
                        loadMasterItems()
                        snackbarHostState.showSnackbar("Master prep item updated successfully")
                    }
                }
            }
        )
    }

    itemToDelete?.let { item ->
        AlertDialog(
            onDismissRequest = { itemToDelete = null },
            title = { Text("Delete Master Prep Item") },
            text = { Text("Are you sure you want to delete '${item.itemName}' from the master catalog?") },
            confirmButton = {
                Button(
                    onClick = {
                        val itemRem = item
                        itemToDelete = null
                        scope.launch {
                            repository.deleteMasterPrepItem(itemRem.id).onSuccess {
                                loadMasterItems()
                                snackbarHostState.showSnackbar("Master prep item deleted")
                            }
                        }
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
                ) {
                    Text("Delete")
                }
            },
            dismissButton = {
                TextButton(onClick = { itemToDelete = null }) {
                    Text("Cancel")
                }
            },
            shape = MaterialTheme.shapes.large
        )
    }
}

@Composable
fun MasterPrepItemDialog(
    title: String,
    initialItem: PrepItem? = null,
    onDismiss: () -> Unit,
    onConfirm: (String, String, String, String) -> Unit
) {
    var name by remember { mutableStateOf(initialItem?.itemName ?: "") }
    var shelfLife by remember { mutableStateOf(initialItem?.shelfLife ?: "") }
    var prepTime by remember { mutableStateOf(initialItem?.prepTimeHour ?: "") }
    var notes by remember { mutableStateOf(initialItem?.notes ?: "") }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(title) },
        text = {
            Column(
                modifier = Modifier.verticalScroll(rememberScrollState()).fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text("Item Name") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = MaterialTheme.shapes.small
                )
                OutlinedTextField(
                    value = shelfLife,
                    onValueChange = { shelfLife = it },
                    label = { Text("Shelf Life (e.g. 3 days)") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = MaterialTheme.shapes.small
                )
                OutlinedTextField(
                    value = prepTime,
                    onValueChange = { prepTime = it },
                    label = { Text("Estimated Prep Time (e.g. 15 mins)") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = MaterialTheme.shapes.small
                )
                OutlinedTextField(
                    value = notes,
                    onValueChange = { notes = it },
                    label = { Text("Notes / Instructions") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = MaterialTheme.shapes.small
                )
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    if (name.isNotBlank()) {
                        onConfirm(name, shelfLife, prepTime, notes)
                    }
                },
                shape = MaterialTheme.shapes.medium
            ) {
                Text("Confirm")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        },
        shape = MaterialTheme.shapes.large
    )
}
